import { useId, useLayoutEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { eventLabel } from '../../../whatsapp/inboxLogic';
import { dayKey, dayLabel, elapsedLabel, formatRemaining, timeLabel, windowRemaining } from '../../../whatsapp/clock';
import { MAX_BODY, charCount } from '../../../whatsapp/composer';
import type { ComposerState } from '../../../whatsapp/composer';
import { AttachmentContent } from './Attachment';
import type {
  Conversation,
  ConversationDetail,
  ThreadItem,
  ThreadMessage,
} from '../../../types/whatsapp';

export interface ThreadPanelProps {
  conversation: Conversation;
  detail: ConversationDetail | null;
  items: ThreadItem[];
  hasMore: boolean;
  loading: boolean;
  timeZone: string;
  nowMs: number;
  onLoadOlder: () => void;
  composer: ComposerState;
  draft: string;
  onDraftChange: (text: string) => void;
  /** From composer.canSend(): blank, too long, sending or closed. */
  canSend: boolean;
  /** A send is running. */
  sending: boolean;
  onSend: () => void;
  /** Message ids whose retry is running. */
  retryingIds: ReadonlySet<number>;
  onRetry: (messageId: number) => void;
  /** A take or return is running. */
  assigning: boolean;
  onTake: () => void;
  onReturn: () => void;
  /** Line shown under the composer. */
  actionError: string | null;
  /** Narrow screens: back to the list. */
  onBack?: () => void;
  /** Narrow screens: opens the context (patient, reason) the side panel shows on wide ones. */
  onOpenContext?: () => void;
}

/** Counter appears from this many characters, so the limit is visible before it is hit. */
const COUNTER_FROM = 3500;

/** Distance from the bottom (px) under which the thread still follows new messages. */
const NEAR_BOTTOM_PX = 80;

const SKELETON = [
  { side: 'self-start', width: 'w-[46%]', height: 'h-12' },
  { side: 'self-end', width: 'w-[40%]', height: 'h-10' },
  { side: 'self-start', width: 'w-[58%]', height: 'h-16' },
];

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const SparkIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  </svg>
);

const PersonIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const CheckIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const DoubleCheckIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M18 6 7 17l-5-5" />
    <path d="m22 10-7.5 7.5L13 16" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const MapPinIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ClockIcon = () => (
  <svg {...ICON_PROPS} width={14} height={14}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg {...ICON_PROPS} width={18} height={18}>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7Z" />
  </svg>
);

const initialsOf = (title: string): string => {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (!/\p{L}/u.test(words[0])) return '+5';
  return words
    .slice(0, 2)
    .map((w) => w.match(/\p{L}/u)?.[0] ?? '')
    .join('')
    .toUpperCase();
};

const itemKey = (item: ThreadItem): string => `${item.kind}-${item.id}`;

const Separator = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-3 text-xs font-medium text-stone-600">
    <span className="h-px flex-1 bg-stone-200" />
    <span className="flex flex-none items-center gap-2 text-center">{children}</span>
    <span className="h-px flex-1 bg-stone-200" />
  </div>
);

const MessageContent = ({ message, conversationId }: { message: ThreadMessage; conversationId: number }) => {
  const { media, type, body } = message;

  if (media && !media.available) return <AttachmentContent conversationId={conversationId} message={message} />;

  switch (type) {
    case 'text':
      return <span>{body}</span>;
    case 'image':
    case 'sticker':
    case 'video':
    case 'audio':
    case 'document':
      return <AttachmentContent conversationId={conversationId} message={message} />;
    case 'location':
      return (
        <span className="flex items-center gap-2 text-sm font-medium">
          <MapPinIcon />
          Ubicación compartida
        </span>
      );
    case 'unsupported':
      return <span className="italic text-stone-600">Mensaje no compatible</span>;
    default:
      return null;
  }
};

/** Retry for a failed staff message. Assistant failures are not retried from here. */
interface RetryProps {
  retrying: boolean;
  /** Why retry is unavailable right now (the mode blocks it), or null when it is available. */
  blockedReason: string | null;
  onRetry: () => void;
}

const Delivery = ({ message, retry }: { message: ThreadMessage; retry: RetryProps }) => {
  switch (message.status) {
    case 'read':
      return (
        <span className="inline-flex items-center gap-1 font-medium text-blue-600">
          <DoubleCheckIcon />
          Leído
        </span>
      );
    case 'delivered':
      return (
        <span className="inline-flex items-center gap-1 text-stone-600">
          <DoubleCheckIcon />
          Entregado
        </span>
      );
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1 text-stone-600">
          <CheckIcon />
          Enviado
        </span>
      );
    case 'pending':
      return <span className="text-stone-600">Enviando</span>;
    case 'failed':
      return (
        <>
          <span
            title={message.error_detail ?? undefined}
            className="inline-flex items-center gap-1 font-semibold text-red-700"
          >
            <AlertCircleIcon />
            No se envió
          </span>
          {message.sender === 'staff' && (
            <button
              type="button"
              onClick={retry.onRetry}
              disabled={retry.retrying || retry.blockedReason !== null}
              title={retry.blockedReason ?? undefined}
              className="inline-flex h-7 items-center rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {retry.retrying ? 'Reintentando…' : 'Reintentar'}
            </button>
          )}
        </>
      );
    default:
      return null;
  }
};

const MessageItem = ({
  message,
  conversationId,
  patientName,
  timeZone,
  retry,
}: {
  message: ThreadMessage;
  conversationId: number;
  patientName: string;
  timeZone: string;
  retry: RetryProps;
}) => {
  const outbound = message.direction === 'outbound';
  const isAssistant = message.sender === 'assistant';
  const label =
    message.sender === 'patient'
      ? (message.sender_name ?? patientName)
      : isAssistant
        ? 'Asistente'
        : `${message.sender_name ?? 'Equipo'} · Equipo`;

  const bubbleTone = message.sender === 'patient'
    ? 'border border-stone-200 bg-white text-stone-800'
    : isAssistant
      ? 'border border-blue-100 bg-blue-50 text-blue-900'
      : 'border border-blue-600 bg-blue-600 text-white';

  // Captions: the body of a non-text message, or the body when the media is unavailable.
  const showCaption =
    message.body !== '' && (message.type !== 'text' || (message.media !== null && !message.media.available));

  return (
    <div className={`flex flex-col gap-1 ${outbound ? 'items-end' : 'items-start'}`}>
      <span className="text-xs font-medium text-stone-600">{label}</span>
      <div
        className={`flex max-w-[78%] flex-col gap-2 whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-[14px] leading-[1.45] ${bubbleTone}`}
      >
        <MessageContent message={message} conversationId={conversationId} />
        {showCaption && <span>{message.body}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
        <span className="font-geist-mono text-xs text-stone-600">{timeLabel(message.created_at, timeZone)}</span>
        {outbound && <Delivery message={message} retry={retry} />}
        {message.topic_label && (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 font-geist-mono text-[11px] text-blue-700">
            {message.topic_label}
          </span>
        )}
      </div>
    </div>
  );
};

const WindowBanner = ({ conversation, nowMs }: { conversation: Conversation; nowMs: number }) => {
  const remaining = windowRemaining(conversation.window.closes_at, nowMs);
  const open = conversation.window.open && remaining.open;

  if (!open) {
    const closedAgo = elapsedLabel(conversation.window.closes_at, nowMs);
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-100 px-5 py-2.5 text-[13px] text-amber-900">
        <span className="font-bold">Ventana de respuesta cerrada</span>
        {closedAgo && (
          <span className="rounded-full bg-amber-200 px-2.5 py-0.5 font-geist-mono text-xs text-amber-900">
            {closedAgo}
          </span>
        )}
        <span>Podrás escribir cuando el paciente vuelva a hacerlo.</span>
      </div>
    );
  }

  const handoffCopy =
    conversation.assigned_to === 'human-agent'
      ? 'Al vencer, la conversación vuelve al asistente y solo se pueden enviar plantillas aprobadas.'
      : 'Al vencer, solo se pueden enviar plantillas aprobadas.';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-stone-200 bg-stone-50 px-5 py-2.5 text-[13px] text-stone-700">
      <span className="font-medium">Ventana de respuesta libre abierta</span>
      <span className="rounded-full bg-green-100 px-2.5 py-0.5 font-geist-mono text-xs text-green-800">
        quedan {formatRemaining(remaining)}
      </span>
      <span className="text-stone-600 max-lg:hidden">{handoffCopy}</span>
    </div>
  );
};

const SkeletonBubbles = () => (
  <div className="flex flex-col gap-3.5" aria-hidden="true">
    {SKELETON.map((s, i) => (
      <div key={i} className={`animate-pulse rounded-2xl bg-stone-100 ${s.side} ${s.width} ${s.height}`} />
    ))}
  </div>
);

export const ThreadPanel = ({
  conversation,
  detail,
  items,
  hasMore,
  loading,
  timeZone,
  nowMs,
  onLoadOlder,
  composer,
  draft,
  onDraftChange,
  canSend,
  sending,
  onSend,
  retryingIds,
  onRetry,
  assigning,
  onTake,
  onReturn,
  actionError,
  onBack,
  onOpenContext,
}: ThreadPanelProps) => {
  const takeHelperId = useId();
  const textareaId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const snapshotRef = useRef<{
    convId: number;
    firstKey: string | null;
    lastKey: string | null;
    scrollHeight: number;
  } | null>(null);

  const firstKey = items.length > 0 ? itemKey(items[0]) : null;
  const lastKey = items.length > 0 ? itemKey(items[items.length - 1]) : null;

  // Runs after every render: follows new items at the bottom, keeps the position when older items are prepended.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prev = snapshotRef.current;
    if (!prev || prev.convId !== conversation.id) {
      nearBottomRef.current = true;
      el.scrollTop = el.scrollHeight;
    } else if (prev.lastKey !== lastKey) {
      if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
    } else if (prev.firstKey !== firstKey) {
      el.scrollTop += el.scrollHeight - prev.scrollHeight;
    }
    snapshotRef.current = { convId: conversation.id, firstKey, lastKey, scrollHeight: el.scrollHeight };
  });

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const patientName = conversation.contact_name || conversation.title;
  const assignedToAgent = conversation.assigned_to === 'human-agent';
  const showSkeleton = detail === null && items.length === 0 && loading;
  const showNoPatient = conversation.patient === null && conversation.contact_name !== '';

  const retryBlockedReason =
    composer.mode === 'disconnected'
      ? 'WhatsApp está desconectado'
      : composer.mode === 'closed'
        ? 'La ventana de 24 h está cerrada'
        : null;

  const actionBase =
    'inline-flex h-10 flex-none items-center gap-1.5 rounded-full border px-4 text-sm font-semibold disabled:cursor-wait disabled:opacity-60';
  // Built twice (wide header and narrow strip): the helper id must differ.
  const buildHeaderAction = (helperId: string): ReactNode => {
    if (composer.canReturn) {
      return (
        <button
          type="button"
          onClick={onReturn}
          disabled={assigning}
          aria-busy={assigning || undefined}
          className={`${actionBase} border-blue-600 bg-white text-blue-700 hover:bg-blue-50`}
        >
          Devolver al asistente
        </button>
      );
    } else if (composer.canTake) {
      return (
        <button
          type="button"
          onClick={onTake}
          disabled={assigning}
          aria-busy={assigning || undefined}
          className={`${actionBase} border-blue-600 bg-white text-blue-700 hover:bg-blue-50`}
        >
          Tomar conversación
        </button>
      );
    } else if (composer.mode === 'closed') {
      return (
        <div className="flex max-w-[260px] flex-col items-end gap-1">
          <button
            type="button"
            aria-disabled="true"
            aria-describedby={composer.takeHelper ? helperId : undefined}
            className="inline-flex h-10 flex-none cursor-not-allowed items-center gap-1.5 rounded-full border border-stone-200 bg-stone-100 px-4 text-sm font-semibold text-stone-500"
          >
            <ClockIcon />
            Tomar conversación
          </button>
          {composer.takeHelper && (
            <p id={helperId} className="m-0 text-right text-xs text-amber-900">
              {composer.takeHelper}
            </p>
          )}
        </div>
      );
    }
    return null;
  };
  const headerAction = buildHeaderAction(takeHelperId);
  const headerActionNarrow = buildHeaderAction(`${takeHelperId}-narrow`);

  const hintText = actionError ?? (composer.mode === 'loading' ? null : composer.hint);
  const count = charCount(draft);

  // Ctrl/Cmd+Enter sends; plain Enter inserts a newline. IME composition is left alone.
  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey) || event.nativeEvent.isComposing) return;
    if (!canSend) return;
    event.preventDefault();
    onSend();
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden border-stone-200 bg-white max-lg:bg-white lg:rounded-[20px] lg:border">
      <header className="flex flex-col border-b border-stone-200 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-3 lg:px-5 lg:py-4">
        <div className="flex min-w-0 items-center gap-2 px-2 py-2 lg:gap-3 lg:p-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Volver a la bandeja"
              className="flex h-11 w-11 flex-none items-center justify-center rounded-full border-0 bg-transparent text-blue-600 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 lg:hidden"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-stone-200 bg-stone-100 text-[13px] font-semibold text-stone-600 lg:h-11 lg:w-11 lg:text-sm">
            {initialsOf(conversation.title)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[15px] font-semibold text-stone-900 lg:text-base">{conversation.title}</h2>
            <p className="m-0 hidden font-geist-mono text-xs text-stone-600 lg:block">{conversation.display_phone}</p>
            <p
              className={`m-0 flex items-center gap-1.5 text-xs font-semibold lg:hidden ${
                assignedToAgent ? 'text-amber-800' : 'text-blue-700'
              }`}
            >
              {assignedToAgent ? <PersonIcon /> : <SparkIcon />}
              {assignedToAgent ? 'Asignada al equipo' : 'Asignada al asistente'}
            </p>
            {showNoPatient && (
              <p className="m-0 hidden truncate text-xs text-stone-600 lg:block">
                Sin paciente registrado · Perfil de WhatsApp: {conversation.contact_name}
              </p>
            )}
          </div>
          {onOpenContext && (
            <button
              type="button"
              onClick={onOpenContext}
              aria-label="Ver contexto del paciente"
              className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 lg:hidden"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5" />
                <path d="M12 8h.01" />
              </svg>
            </button>
          )}
        </div>
        {headerActionNarrow && (
          <div className="flex items-center justify-end border-t border-stone-200 bg-stone-50 px-3 py-2 lg:hidden">
            {headerActionNarrow}
          </div>
        )}
        <div className="hidden flex-none flex-wrap items-center justify-end gap-x-4 gap-y-2 lg:flex">
          <div className="flex items-center gap-2 text-[13px] text-stone-600">
            <span>Asignada a</span>
            {assignedToAgent ? (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                <PersonIcon />
                Equipo
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-blue-700">
                <SparkIcon />
                Asistente
              </span>
            )}
          </div>
          {headerAction}
        </div>
      </header>

      <WindowBanner conversation={conversation} nowMs={nowMs} />

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4 lg:min-h-[360px] lg:p-5"
      >
        {hasMore && (
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={loading}
            className="self-center text-[13px] font-semibold text-blue-700 hover:underline disabled:text-stone-500 disabled:no-underline"
          >
            Cargar mensajes anteriores
          </button>
        )}

        {showSkeleton && <SkeletonBubbles />}

        {items.map((item, index) => {
          const day = dayKey(Date.parse(item.created_at), timeZone);
          const previous = index > 0 ? items[index - 1] : null;
          const startsDay = previous === null || dayKey(Date.parse(previous.created_at), timeZone) !== day;
          return (
            <div key={itemKey(item)} className="flex flex-col gap-3.5">
              {startsDay && <Separator>{dayLabel(item.created_at, timeZone, nowMs)}</Separator>}
              {item.kind === 'event' ? (
                <Separator>
                  <span>{eventLabel(item)}</span>
                  <span className="font-geist-mono text-[11px] text-stone-500">{timeLabel(item.created_at, timeZone)}</span>
                </Separator>
              ) : (
                <MessageItem
                  message={item}
                  conversationId={conversation.id}
                  patientName={patientName}
                  timeZone={timeZone}
                  retry={{
                    retrying: retryingIds.has(item.id),
                    blockedReason: retryBlockedReason,
                    onRetry: () => onRetry(item.id),
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <footer className="flex flex-col gap-2 border-t border-stone-200 px-3 pb-4 pt-2.5 lg:px-5 lg:py-3">
        <div className="flex items-stretch gap-2 lg:gap-3">
          <label htmlFor={textareaId} className="sr-only">
            Mensaje
          </label>
          <textarea
            id={textareaId}
            rows={2}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onComposerKeyDown}
            disabled={!composer.canType || sending}
            placeholder={composer.mode === 'loading' ? '' : composer.placeholder}
            className="min-w-0 flex-1 resize-none rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm max-lg:h-12 max-lg:rounded-full text-stone-900 placeholder:text-stone-500 focus:border-blue-600 focus:outline-none disabled:cursor-not-allowed disabled:bg-stone-100"
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            aria-label="Enviar mensaje"
            aria-busy={sending || undefined}
            className={`inline-flex h-12 flex-none items-center justify-center gap-2 self-end rounded-full px-5 text-sm font-semibold max-lg:w-12 max-lg:px-0 ${
              canSend ? 'bg-blue-600 text-white hover:bg-blue-700' : 'cursor-not-allowed bg-stone-200 text-stone-600'
            }`}
          >
            <PaperPlaneIcon />
            <span className="max-lg:sr-only">{sending ? 'Enviando…' : 'Enviar'}</span>
          </button>
        </div>
        {(hintText !== null || count > COUNTER_FROM) && (
          <div className="flex items-start gap-3 text-xs">
            {hintText !== null && (
              <p
                role={actionError ? 'alert' : undefined}
                className={`m-0 min-w-0 ${actionError ? 'text-red-700' : 'text-stone-600'}`}
              >
                {hintText}
              </p>
            )}
            {count > COUNTER_FROM && (
              <span
                className={`ml-auto flex-none font-geist-mono ${count > MAX_BODY ? 'text-red-700' : 'text-stone-600'}`}
              >
                {`${count}/${MAX_BODY}`}
              </span>
            )}
          </div>
        )}
      </footer>
    </section>
  );
};
